/**
 * stile.js — la classe dello stile su <html>, e lo stile chiesto al server.
 *
 * L'INTERRUTTORE DELLO STILE (Alex, 14/10/2026): lo stile nuovo «Console» non
 * si vede in clinica finche' non e' completo al 100%. Il server decide per
 * ogni utenza (backend/services/stileNuovo.js) e manda `stile_nuovo` col
 * login e con GET /api/auth/me. Qui:
 *   - <html> porta `stile-console` (acceso) o `stile-classico` (spento, e
 *     anche senza utente: il login, il tablet). I fogli dei due gusci valgono
 *     solo sotto la loro classe (scripts/prefissaStile.py): tutti e due stanno
 *     nello stesso pacchetto e usano gli stessi nomi;
 *   - all'apertura della pagina, con una sessione, si richiede lo stile a
 *     /api/auth/me UNA volta per utente: chi viene acceso o spento con
 *     backend/scripts/stileNuovo.js cambia guscio al primo ricaricamento,
 *     senza dover uscire e rientrare. Se la domanda non riesce resta quello
 *     che c'era (al primo giro dopo il rilascio: lo stile di prima).
 */
import { useEffect, useLayoutEffect } from 'react';
import { useAuthStore } from '../store/useStore';
import api from '../services/api';
import { stileNuovo } from './paginaIniziale';

export const CLASSE_NUOVO = 'stile-console';
export const CLASSE_CLASSICO = 'stile-classico';

/** Mette su <html> la classe dello stile. */
export function applicaClasse(nuovo) {
    if (typeof document === 'undefined') return;
    const radice = document.documentElement;
    radice.classList.toggle(CLASSE_NUOVO, !!nuovo);
    radice.classList.toggle(CLASSE_CLASSICO, !nuovo);
}

/** La classe dall'utente in memoria, prima del primo disegno (App.js). */
export const applicaClasseIniziale = () => applicaClasse(stileNuovo(useAuthStore.getState().user));

/* Una domanda per utente e per pagina caricata, non a ogni disegno. */
const chiesti = new Set();

/** Tiene la classe allineata all'utente e chiede lo stile al server. */
export function useStileDellUtente() {
    const utente = useAuthStore((st) => st.user);
    const autenticato = useAuthStore((st) => st.isAuthenticated);
    const nuovo = stileNuovo(utente);
    useLayoutEffect(() => { applicaClasse(nuovo); }, [nuovo]);

    const id = utente?.id;
    useEffect(() => {
        if (!autenticato || !id || chiesti.has(id)) return undefined;
        /* Senza gettone non si chiede: la risposta sarebbe solo una 401, e la
           401 rimanda all'accesso (il ciclo del 15/10/2026, vedi
           services/api.js). Lo store senza gettone e' una sessione chiusa. */
        let gettone = null;
        try { gettone = localStorage.getItem('token'); } catch (e) { gettone = null; }
        if (!gettone) return undefined;
        chiesti.add(id);
        /* Niente «vivo» legato all'effetto: con StrictMode l'effetto gira due
           volte e la seconda esce subito (chiesti), quindi la risposta la deve
           poter scrivere la prima. Il controllo e' sull'utente di adesso. */
        let domanda;
        try {
            domanda = api && typeof api.get === 'function' ? api.get('/auth/me') : null;
        } catch (e) { domanda = null; }
        if (!domanda || typeof domanda.then !== 'function') return undefined;
        domanda.then((r) => {
            const v = r?.data?.data?.user?.stile_nuovo;
            const ora = useAuthStore.getState();
            if (typeof v !== 'boolean' || ora.user?.id !== id) return;
            if (v !== stileNuovo(ora.user)) ora.aggiornaUser({ stile_nuovo: v });
        }).catch(() => {});
        return undefined;
    }, [autenticato, id]);
}
